import { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  ArrowUpRight,
  Building2,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import moment from "moment";
import { getBookingsDetails } from "../../../../Service/bookings";
import Loader from "../../../../reusableUI/loader";

const TIMEFRAME_OPTIONS = [
  { label: "7D", value: "last7days" },
  { label: "15D", value: "last15days" },
  { label: "30D", value: "last30days" },
  { label: "All time", value: "alltime" },
];

const THEME_PRIMARY = "#4951EC";
const THEME_PRIMARY_SOFT = "#EEF0FF";

const PROCESSED_STATUSES = new Set(["processed", "paid", "completed", "success"]);
const PENDING_STATUSES = new Set(["pending", "initiated"]);

const panelClassName =
  "h-full rounded-xl border border-white/80 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)]";

const amountFormatter = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

const countFormatter = new Intl.NumberFormat("en-IN");

const formatCurrency = (value) => `Rs ${amountFormatter.format(Number(value || 0))}`;

const formatCount = (value) => countFormatter.format(Number(value || 0));

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const getTimeframeLabel = (timeframe) =>
  TIMEFRAME_OPTIONS.find((option) => option.value === timeframe)?.label || "All time";

const normalizeStatusValue = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const getTimeframeStart = (timeframe) => {
  const today = moment().startOf("day");

  switch (timeframe) {
    case "last7days":
      return today.clone().subtract(6, "days");
    case "last15days":
      return today.clone().subtract(14, "days");
    case "last30days":
      return today.clone().subtract(29, "days");
    default:
      return null;
  }
};

const isInTimeframe = (value, timeframe) => {
  const date = moment(value);
  if (!date.isValid()) return false;

  const start = getTimeframeStart(timeframe);
  if (!start) return true;

  return date.isBetween(start, moment().endOf("day"), "day", "[]");
};

const getDutyMoment = (duty) => {
  const transferDate = duty?.payout?.payoutToTheVendor?.transferDateTime;
  if (transferDate) {
    const parsedUtc = moment.utc(
      transferDate,
      ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601],
      true
    );
    if (parsedUtc.isValid()) {
      return parsedUtc.local();
    }

    const parsed = moment(transferDate);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  const endDutyTime = duty?.endDuty?.time?.$date || duty?.endDuty?.time;
  if (endDutyTime) {
    const parsed = moment(endDutyTime);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  if (duty?.date) {
    const parsed = moment(duty.date, "YYYY-MM-DD", true);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  return null;
};

const getDutyAmount = (duty) => {
  const payout = duty?.payout?.payoutToTheVendor;
  return Number(
    payout?.amountToTransfer ?? payout?.netPayout ?? duty?.payout?.amountToTransfer ?? 0
  );
};

const getDutyStatusKey = (duty) => {
  const rawStatus = normalizeStatusValue(
    duty?.payout?.payoutToTheVendor?.status || duty?.payout?.status
  );

  if (PROCESSED_STATUSES.has(rawStatus)) return "processed";
  if (PENDING_STATUSES.has(rawStatus)) return "pending";
  if (rawStatus) return rawStatus;

  return "pending";
};

const getSpecialistName = (visit, duty) =>
  duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo?.fullName ||
  duty?.assignedStaffName ||
  visit?.dutyStaff?.name ||
  visit?.specialistInfo?.name ||
  "Specialist";

const getVisitWindow = (visit) => {
  const startDate = visit?.visitInfo?.startDate;
  const endDate = visit?.visitInfo?.endDate;

  if (startDate && endDate) {
    const startLabel = moment(startDate).format("D MMM");
    const endLabel = moment(endDate).format("D MMM YYYY");
    return startLabel === endLabel ? endLabel : `${startLabel} - ${endLabel}`;
  }

  if (startDate) return moment(startDate).format("D MMM YYYY");
  if (endDate) return moment(endDate).format("D MMM YYYY");
  return "-";
};

const isVisitActiveToday = (visit) => {
  const startDate = visit?.visitInfo?.startDate;
  const endDate = visit?.visitInfo?.endDate;
  if (!startDate || !endDate) return false;

  const today = moment().startOf("day");
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");

  if (!start.isValid() || !end.isValid()) return false;
  return today.isBetween(start, end, "day", "[]");
};

const getPartnerIdentity = (visit) => ({
  partnerID: String(visit?.appointmentType?.partnerID || "").trim(),
  partnerName: String(visit?.appointmentType?.partnerName || "").trim(),
});

const createVisitRow = (
  visit,
  categoryKey,
  categoryLabel,
  partnerID,
  partnerName,
  dutyRows
) => {
  const processedAmount = dutyRows
    .filter((duty) => duty.statusKey === "processed")
    .reduce((sum, duty) => sum + duty.amount, 0);
  const pendingAmount = dutyRows
    .filter((duty) => duty.statusKey !== "processed")
    .reduce((sum, duty) => sum + duty.amount, 0);
  const latestDutyMoment = dutyRows.reduce((latest, duty) => {
    if (!latest || duty.moment.isAfter(latest)) {
      return duty.moment;
    }
    return latest;
  }, null);
  const processedDutyCount = dutyRows.filter((duty) => duty.statusKey === "processed").length;
  const pendingDutyCount = dutyRows.filter((duty) => duty.statusKey !== "processed").length;

  return {
    rowID: String(visit?._id || visit?.jobID || `${categoryKey}-${partnerName}`),
    visitID: String(visit?._id || ""),
    jobID: visit?.jobID || "-",
    patientName: visit?.patient?.name || "-",
    categoryKey,
    categoryLabel,
    partnerID,
    partnerName,
    isActiveToday: isVisitActiveToday(visit),
    visitWindow: getVisitWindow(visit),
    dutyType: visit?.dutyType || "-",
    processedAmount,
    pendingAmount,
    totalAmount: processedAmount + pendingAmount,
    processedDutyCount,
    pendingDutyCount,
    latestDutyMoment,
    latestDutyLabel: latestDutyMoment ? latestDutyMoment.format("D MMM YYYY") : "-",
    dutyRows: dutyRows
      .slice()
      .sort((left, right) => right.moment.valueOf() - left.moment.valueOf()),
  };
};

const buildBaseVisitRows = (bookings = [], timeframe = "alltime") =>
  bookings
    .map((visit) => {
      const appointmentType = String(visit?.appointmentType?.type || "self")
        .trim()
        .toLowerCase();
      const categoryKey = appointmentType === "partner" ? "partner" : "individual";
      const categoryLabel = categoryKey === "partner" ? "Partner" : "Individual";
      const partnerIdentity = getPartnerIdentity(visit);
      const partnerID = categoryKey === "partner" ? partnerIdentity.partnerID : "";
      const partnerName =
        categoryKey === "partner"
          ? partnerIdentity.partnerName || "Partner-managed visit"
          : "Individual visit";

      const dutyRows = (Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [])
        .map((duty, index) => {
          const momentValue = getDutyMoment(duty);
          if (!momentValue || !isInTimeframe(momentValue, timeframe)) {
            return null;
          }

          const amount = getDutyAmount(duty);
          const statusKey = getDutyStatusKey(duty);
          if (amount <= 0 && statusKey === "pending") {
            return null;
          }

          return {
            key: `${visit?._id || visit?.jobID || "visit"}-${duty?.date || index}`,
            dateLabel: momentValue.format("D MMM YYYY"),
            moment: momentValue,
            amount,
            statusKey,
            rawStatus:
              duty?.payout?.payoutToTheVendor?.status || duty?.payout?.status || "Pending",
            bonus: Number(duty?.payout?.payoutToTheVendor?.bonus || 0),
            penalty: Number(duty?.payout?.payoutToTheVendor?.penalty || 0),
            specialistName: getSpecialistName(visit, duty),
            dutyType: duty?.type || visit?.dutyType || "-",
          };
        })
        .filter(Boolean);

      if (!dutyRows.length) return null;

      return createVisitRow(
        visit,
        categoryKey,
        categoryLabel,
        partnerID,
        partnerName,
        dutyRows
      );
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (right.processedAmount !== left.processedAmount) {
        return right.processedAmount - left.processedAmount;
      }

      return (right.latestDutyMoment?.valueOf() || 0) - (left.latestDutyMoment?.valueOf() || 0);
    });

const buildTrendData = (rows = [], timeframe = "alltime") => {
  const dutyRows = rows.flatMap((row) =>
    row.dutyRows.map((duty) => ({
      ...duty,
      categoryKey: row.categoryKey,
    }))
  );

  if (!dutyRows.length) {
    return {
      categories: [],
      series: [],
    };
  }

  const granularity = timeframe === "alltime" ? "month" : "day";
  const bucketMap = new Map();

  if (granularity === "day") {
    const start = getTimeframeStart(timeframe) || dutyRows[0].moment.clone().startOf("day");
    const end = moment().endOf("day");
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end, "day")) {
      const key = cursor.format("YYYY-MM-DD");
      bucketMap.set(key, {
        label: cursor.format("D MMM"),
        partner: 0,
        individual: 0,
      });
      cursor.add(1, "day");
    }

    dutyRows.forEach((duty) => {
      const key = duty.moment.format("YYYY-MM-DD");
      if (!bucketMap.has(key)) return;
      bucketMap.get(key)[duty.categoryKey] += duty.amount;
    });
  } else {
    const months = dutyRows
      .map((duty) => duty.moment.clone().startOf("month"))
      .sort((left, right) => left.valueOf() - right.valueOf());
    const start = months[0].clone();
    const end = months[months.length - 1].clone();
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end, "month")) {
      const key = cursor.format("YYYY-MM");
      bucketMap.set(key, {
        label: cursor.format("MMM YYYY"),
        partner: 0,
        individual: 0,
      });
      cursor.add(1, "month");
    }

    dutyRows.forEach((duty) => {
      const key = duty.moment.format("YYYY-MM");
      if (!bucketMap.has(key)) return;
      bucketMap.get(key)[duty.categoryKey] += duty.amount;
    });
  }

  const buckets = Array.from(bucketMap.values());

  return {
    categories: buckets.map((bucket) => bucket.label),
    series: [
      {
        name: "Partner",
        data: buckets.map((bucket) => Number(bucket.partner.toFixed(2))),
        color: THEME_PRIMARY,
      },
      {
        name: "Individual",
        data: buckets.map((bucket) => Number(bucket.individual.toFixed(2))),
        color: "#7C83FF",
      },
    ],
  };
};

const buildDashboard = (rows = []) => {
  const totals = rows.reduce(
    (accumulator, row) => {
      accumulator.totalCredited += row.processedAmount;
      accumulator.totalPending += row.pendingAmount;
      accumulator.totalVisits += 1;
      accumulator.creditedDutyDays += row.processedDutyCount;

      if (row.categoryKey === "partner") {
        accumulator.partnerCredited += row.processedAmount;
      } else {
        accumulator.individualCredited += row.processedAmount;
      }

      return accumulator;
    },
    {
      totalCredited: 0,
      totalPending: 0,
      partnerCredited: 0,
      individualCredited: 0,
      totalVisits: 0,
      creditedDutyDays: 0,
    }
  );

  const categoryBreakdown = [
    {
      key: "partner",
      label: "Partner",
      amount: totals.partnerCredited,
      share:
        totals.totalCredited > 0 ? (totals.partnerCredited / totals.totalCredited) * 100 : 0,
      count: rows.filter((row) => row.categoryKey === "partner").length,
      color: THEME_PRIMARY,
    },
    {
      key: "individual",
      label: "Individual",
      amount: totals.individualCredited,
      share:
        totals.totalCredited > 0
          ? (totals.individualCredited / totals.totalCredited) * 100
          : 0,
      count: rows.filter((row) => row.categoryKey === "individual").length,
      color: "#7C83FF",
    },
  ];

  const partnerBuckets = new Map();
  rows
    .filter((row) => row.categoryKey === "partner")
    .forEach((row) => {
      const partnerKey = row.partnerID || row.partnerName;

      if (!partnerBuckets.has(partnerKey)) {
        partnerBuckets.set(partnerKey, {
          partnerID: row.partnerID,
          partnerName: row.partnerName,
          creditedAmount: 0,
          pendingAmount: 0,
          visitCount: 0,
        });
      }

      const bucket = partnerBuckets.get(partnerKey);
      if (!bucket.partnerName && row.partnerName) {
        bucket.partnerName = row.partnerName;
      }
      bucket.creditedAmount += row.processedAmount;
      bucket.pendingAmount += row.pendingAmount;
      bucket.visitCount += 1;
    });

  const topPartners = Array.from(partnerBuckets.values())
    .sort((left, right) => right.creditedAmount - left.creditedAmount)
    .slice(0, 6);

  const activePartners = topPartners.filter((partner) => partner.creditedAmount > 0).length;
  const leadingPartner = topPartners[0];
  const partnerShare = categoryBreakdown[0].share;

  const insights = [
    `${formatCurrency(totals.partnerCredited)} has been credited through Partner visits, contributing ${formatPercent(partnerShare)} of the visible payout value.`,
    leadingPartner
      ? `${leadingPartner.partnerName} is the highest payout partner at ${formatCurrency(leadingPartner.creditedAmount)} in the selected window.`
      : "No partner-managed visit credits landed in the selected window.",
    `${formatCount(totals.creditedDutyDays)} duty days have already been credited, while ${formatCurrency(totals.totalPending)} still sits outside processed transfer status.`,
  ];

  return {
    totals,
    categoryBreakdown,
    topPartners,
    activePartners,
    insights,
  };
};

const SectionHeader = ({ title, subtitle, action }) => (
  <div className="mb-5 flex items-start justify-between gap-4">
    <div>
      <h3 className="text-base font-semibold tracking-[-0.03em] text-slate-900">
        {title}
      </h3>
      {subtitle ? (
        <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p>
      ) : null}
    </div>
    {action}
  </div>
);

const StatCard = ({ icon: Icon, eyebrow, value, detail, accentClass }) => (
  <div className="rounded-xl border border-white/80 bg-white p-5 shadow-[0_16px_45px_rgba(15,23,42,0.08)]">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {eyebrow}
        </p>
        <p className="mt-3 text-xl font-semibold tracking-[-0.04em] text-slate-900">
          {value}
        </p>
      </div>
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-2xl ${accentClass}`}
      >
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <p className="text-xs leading-5 text-slate-500">{detail}</p>
  </div>
);

const EmptyState = ({ title, body }) => (
  <div className="flex h-full min-h-[180px] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center">
    <div>
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="mt-2 text-xs leading-5 text-slate-500">{body}</p>
    </div>
  </div>
);

const TrendChart = ({ trendData }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "column",
        backgroundColor: "transparent",
        height: 250,
        spacingTop: 8,
        spacingLeft: 0,
        spacingRight: 0,
      },
      title: { text: null },
      xAxis: {
        categories: trendData.categories,
        lineColor: "#D7E4E1",
        labels: {
          style: {
            color: "#64748B",
            fontSize: "11px",
          },
        },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#E2ECE9",
        labels: {
          formatter() {
            return formatCurrency(this.value);
          },
          style: {
            color: "#64748B",
            fontSize: "11px",
          },
        },
      },
      legend: {
        align: "left",
        verticalAlign: "top",
        itemStyle: {
          color: "#334155",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      tooltip: {
        shared: true,
        backgroundColor: "#0F172A",
        borderWidth: 0,
        style: {
          color: "#F8FAFC",
        },
        pointFormatter() {
          return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formatCurrency(this.y)}</b><br/>`;
        },
      },
      plotOptions: {
        column: {
          borderRadius: 12,
          pointPadding: 0.18,
          stacking: "normal",
        },
        series: {
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      series: trendData.series,
      credits: { enabled: false },
    }),
    [trendData]
  );

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const CategoryShareChart = ({ categories }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: 220,
        spacing: [0, 0, 0, 0],
      },
      title: { text: null },
      plotOptions: {
        pie: {
          innerSize: "72%",
          borderWidth: 0,
          dataLabels: {
            enabled: false,
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      tooltip: {
        pointFormatter() {
          return `<b>${formatCurrency(this.y)}</b>`;
        },
      },
      series: [
        {
          name: "Credited amount",
          data: categories.map((category) => ({
            name: category.label,
            y: Number(category.amount.toFixed(2)),
            color: category.color,
          })),
        },
      ],
      credits: { enabled: false },
    }),
    [categories]
  );

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const TrendSummaryStrip = ({ dashboard }) => {
  const leadingPartner = dashboard.topPartners[0];
  const items = [
    {
      label: "Credited visits",
      value: formatCount(
        dashboard.categoryBreakdown.reduce((sum, item) => sum + item.count, 0)
      ),
      accent: "text-[#0F172A]",
    },
    {
      label: "Active partners",
      value: formatCount(dashboard.activePartners),
      accent: "text-[#4951EC]",
    },
    {
      label: "Pending amount",
      value: formatCurrency(dashboard.totals.totalPending),
      accent: "text-[#C2410C]",
    },
    {
      label: "Leading partner",
      value: leadingPartner
        ? `${leadingPartner.partnerName} (${formatCurrency(leadingPartner.creditedAmount)})`
        : "No partner credit",
      accent: "text-[#4951EC]",
    },
  ];

  return (
    <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {item.label}
          </p>
          <p
            className={`mt-2 text-base font-semibold tracking-[-0.03em] ${item.accent}`}
          >
            {item.value}
          </p>
        </div>
      ))}
    </div>
  );
};

const CategoryMixPanel = ({ categories }) => (
  <div className={panelClassName}>
    <SectionHeader
      title="Partner vs individual mix"
      subtitle="How credited visit value is split between Partner and Individual flows."
    />
    {categories.some((category) => category.amount > 0) ? (
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_230px] lg:items-center">
        <CategoryShareChart categories={categories} />
        <div className="space-y-3">
          {categories.map((category) => (
            <div
              key={category.key}
              className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-4"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="text-base font-medium text-slate-700">
                    {category.label}
                  </span>
                </div>
                <span className="text-base font-semibold text-slate-900">
                  {formatPercent(category.share)}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-500">
                {formatCurrency(category.amount)}
              </p>
            </div>
          ))}
        </div>
      </div>
    ) : (
      <EmptyState
        title="No credited value available"
        body="Category mix appears once duty-level credits fall into the selected window."
      />
    )}
  </div>
);

const TopPartnerPanel = ({ partners }) => (
  <div className={panelClassName}>
    <SectionHeader
      title="Top partner credits"
      subtitle="Which partners have the highest credited visit value in this window."
    />
    {partners.length ? (
      <div className="space-y-3">
        {partners.map((partner, index) => (
          <div
            key={`${partner.partnerName}-${index}`}
            className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">
                  {partner.partnerName}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span className="rounded-full bg-white px-2 py-1">
                    {formatCount(partner.visitCount)} visits
                  </span>
                  {partner.pendingAmount > 0 ? (
                    <span className="rounded-full bg-[#FFF1E7] px-2 py-1 text-[#C2410C]">
                      Pending {formatCurrency(partner.pendingAmount)}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-900">
                  {formatCurrency(partner.creditedAmount)}
                </p>
                <p className="mt-1 text-xs text-slate-400">Credited value</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <EmptyState
        title="No partner credits in this window"
        body="Partner ranking will appear once partner-managed visits receive credited duty payouts."
      />
    )}
  </div>
);

const InsightPanel = ({ insights, timeframe }) => (
  <div className={panelClassName}>
    <SectionHeader
      title="Computed insights"
      subtitle="Deterministic readouts based on duty-level credits."
      action={
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          {getTimeframeLabel(timeframe)}
        </span>
      }
    />
    <div className="grid gap-3 xl:grid-cols-3">
      {insights.map((item, index) => (
        <div key={`${item}-${index}`} className="rounded-xl bg-slate-50 px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
            Insight {index + 1}
          </p>
          <p className="mt-3 text-xs leading-5 text-slate-700">{item}</p>
        </div>
      ))}
    </div>
  </div>
);

const DutyBreakdownTable = ({ dutyRows }) => (
  <div className="overflow-x-auto">
    <table className="min-w-[880px] w-full overflow-hidden rounded-xl border border-slate-100 bg-white text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-400">
        <tr>
          <th className="px-4 py-3 font-semibold">Date</th>
          <th className="px-4 py-3 font-semibold">Specialist</th>
          <th className="px-4 py-3 font-semibold">Duty type</th>
          <th className="px-4 py-3 font-semibold text-right">Bonus</th>
          <th className="px-4 py-3 font-semibold text-right">Penalty</th>
          <th className="px-4 py-3 font-semibold">Status</th>
          <th className="px-4 py-3 font-semibold text-right">Credited amount</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {dutyRows.map((duty) => (
          <tr key={duty.key} className="text-slate-600">
            <td className="px-4 py-3 font-medium text-slate-900">{duty.dateLabel}</td>
            <td className="px-4 py-3">{duty.specialistName}</td>
            <td className="px-4 py-3 capitalize">{String(duty.dutyType || "-")}</td>
            <td className="px-4 py-3 text-right">{formatCurrency(duty.bonus)}</td>
            <td className="px-4 py-3 text-right">{formatCurrency(duty.penalty)}</td>
            <td className="px-4 py-3">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  duty.statusKey === "processed"
                    ? "bg-emerald-50 text-emerald-600"
                    : "bg-amber-50 text-amber-600"
                }`}
              >
                {duty.statusKey === "processed" ? "Credited" : "Pending"}
              </span>
            </td>
            <td className="px-4 py-3 text-right font-semibold text-slate-900">
              {formatCurrency(duty.amount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const VisitRow = ({ row, expanded, onToggle }) => {
  const summaryStatus =
    row.isActiveToday
      ? "Ongoing"
      : row.pendingAmount > 0 && row.processedAmount > 0
        ? "Partially credited"
      : row.processedAmount > 0
        ? "Credited"
        : "Pending";

  return (
    <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{row.jobID}</span>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                row.categoryKey === "partner"
                  ? "bg-[#EEF0FF] text-[#4951EC]"
                  : "bg-[#EEF0FF] text-[#4951EC]"
              }`}
            >
              {row.categoryLabel}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                summaryStatus === "Ongoing"
                  ? "bg-[#EEF0FF] text-[#4951EC]"
                  : summaryStatus === "Credited"
                    ? "bg-[#EEF0FF] text-[#4951EC]"
                  : summaryStatus === "Partially credited"
                    ? "bg-[#FFF1E7] text-[#C2410C]"
                    : "bg-amber-50 text-amber-600"
              }`}
            >
              {summaryStatus}
            </span>
          </div>

          <div className="mt-2 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Owner</p>
              <p className="mt-1 text-sm font-medium text-slate-700">{row.partnerName}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Patient</p>
              <p className="mt-1 text-sm font-medium text-slate-700">{row.patientName}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Visit window</p>
              <p className="mt-1 text-sm font-medium text-slate-700">{row.visitWindow}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Latest credit</p>
              <p className="mt-1 text-sm font-medium text-slate-700">{row.latestDutyLabel}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 xl:items-end">
          <div className="text-left xl:text-right">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Credited amount</p>
            <p className="mt-1 text-base font-semibold text-slate-900">
              {formatCurrency(row.processedAmount)}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Pending {formatCurrency(row.pendingAmount)}
            </p>
          </div>

          <button
            type="button"
            onClick={() => onToggle(row.rowID)}
            className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
          >
            {expanded ? "Hide details" : "View details"}
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
          <div className="mb-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl bg-white px-4 py-3">
              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Credited days</p>
              <p className="mt-2 text-sm font-medium text-slate-700">
                {formatCount(row.processedDutyCount)}
              </p>
            </div>
            <div className="rounded-xl bg-white px-4 py-3">
              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Pending days</p>
              <p className="mt-2 text-sm font-medium text-slate-700">
                {formatCount(row.pendingDutyCount)}
              </p>
            </div>
            <div className="rounded-xl bg-white px-4 py-3">
              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Route type</p>
              <p className="mt-2 text-sm font-medium text-slate-700">{row.categoryLabel}</p>
            </div>
          </div>
          <DutyBreakdownTable dutyRows={row.dutyRows} />
        </div>
      ) : null}
    </div>
  );
};

const Payment = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeframe, setTimeframe] = useState("alltime");

  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const bookingResponse = await getBookingsDetails();
        if (isCancelled) return;

        if (bookingResponse?.error) {
          setBookings([]);
          setError("Unable to load payment insights right now.");
          return;
        }

        const nextBookings = Array.isArray(bookingResponse?.data?.bookings)
          ? bookingResponse.data.bookings
          : [];
        setBookings(nextBookings);
      } catch (fetchError) {
        if (!isCancelled) {
          setBookings([]);
          setError("Unable to load payment insights right now.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, []);

  const baseRows = useMemo(() => buildBaseVisitRows(bookings, timeframe), [bookings, timeframe]);

  const visibleRows = useMemo(() => baseRows, [baseRows]);

  const dashboard = useMemo(() => buildDashboard(visibleRows), [visibleRows]);
  const trendData = useMemo(() => buildTrendData(visibleRows, timeframe), [visibleRows, timeframe]);

  const toggleRowExpansion = (rowID) => {
    setExpandedRows((previous) => ({
      ...previous,
      [rowID]: !previous[rowID],
    }));
  };

  const cards = [
    {
      eyebrow: "Total credited",
      value: formatCurrency(dashboard.totals.totalCredited),
      detail: `${formatCurrency(dashboard.totals.totalPending)} remains outside processed transfer status.`,
      icon: CircleDollarSign,
      accentClass: "bg-[#EEF0FF] text-[#4951EC]",
    },
    {
      eyebrow: "Partner credited",
      value: formatCurrency(dashboard.totals.partnerCredited),
      detail: "Total credited amount attached to partner-managed visits.",
      icon: Building2,
      accentClass: "bg-[#EEF0FF] text-[#4951EC]",
    },
    {
      eyebrow: "Individual credited",
      value: formatCurrency(dashboard.totals.individualCredited),
      detail: "Total credited amount attached to directly managed individual visits.",
      icon: UserRound,
      accentClass: "bg-[#EEF0FF] text-[#4951EC]",
    },
    {
      eyebrow: "Credited duty days",
      value: formatCount(dashboard.totals.creditedDutyDays),
      detail: `${formatCount(dashboard.totals.totalVisits)} visits contributed visible payment records in this filter.`,
      icon: ShieldCheck,
      accentClass: "bg-[#FFEDD5] text-[#C2410C]",
    },
  ];

  if (loading) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="overflow-y-auto bg-[#F3F6F5] w-full h-[90vh] overflow-x-hidden mb-20">
      <div className="flex flex-row flex-wrap items-center justify-between gap-y-2 px-4 py-4 bg-white shadow-sm mb-2 mx-2 rounded-md">
        <div className="flex flex-row items-center gap-3">
          <h2 className="text-xl font-bold text-gray-800">Payment Operations</h2>
          <span className="relative flex items-center">
            <span className="absolute inline-flex h-2.5 w-2.5 animate-ping rounded-full bg-green-500 opacity-75"></span>
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500"></span>
          </span>
          <span className="text-sm text-gray-600 font-medium">
            {TIMEFRAME_OPTIONS.find((opt) => opt.value === timeframe)?.label === "All time"
              ? "All Time"
              : TIMEFRAME_OPTIONS.find((opt) => opt.value === timeframe)?.label === "7D"
              ? "Last 7 Days"
              : TIMEFRAME_OPTIONS.find((opt) => opt.value === timeframe)?.label === "15D"
              ? "Last 15 Days"
              : TIMEFRAME_OPTIONS.find((opt) => opt.value === timeframe)?.label === "30D"
              ? "Last 30 Days"
              : "All Time"}
          </span>
        </div>

        <div className="flex gap-2">
          {TIMEFRAME_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setTimeframe(option.value)}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                timeframe === option.value
                  ? "bg-[#4951EC] text-white shadow-md shadow-blue-200"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              {option.label === "7D"
                ? "Last 7 Days"
                : option.label === "15D"
                ? "Last 15 Days"
                : option.label === "30D"
                ? "Last 30 Days"
                : "All Time"}
            </button>
          ))}
        </div>
      </div>

      <div className="px-3 pb-12 pt-3 md:px-4 mx-auto max-w-[1480px]">
        {error ? (
          <div className="mb-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {error}
          </div>
        ) : null}

        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500 shadow-sm border border-slate-200">
          <ArrowUpRight className="h-3.5 w-3.5" />
          {formatCount(visibleRows.length)} visits contribute payment records in the selected window
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-4">
          {cards.map((card) => (
            <StatCard
              key={card.eyebrow}
              icon={card.icon}
              eyebrow={card.eyebrow}
              value={card.value}
              detail={card.detail}
              accentClass={card.accentClass}
            />
          ))}
        </div>

        <div className="mt-5 grid items-stretch gap-4 xl:grid-cols-12">
          <div className={`xl:col-span-8 ${panelClassName}`}>
            <SectionHeader
              title="Credits over time"
              subtitle="Duty-level credits grouped by visit route for the selected timeframe."
            />
            {trendData.categories.length ? (
              <>
                <TrendChart trendData={trendData} />
                <TrendSummaryStrip dashboard={dashboard} />
              </>
            ) : (
              <div className="flex h-[320px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                No payment records to chart in this timeframe.
              </div>
            )}
          </div>

          <div className="xl:col-span-4 h-full">
            <CategoryMixPanel categories={dashboard.categoryBreakdown} />
          </div>
        </div>

        <div className="mt-5 grid items-stretch gap-4 xl:grid-cols-12">
          <div className="xl:col-span-4 h-full">
            <TopPartnerPanel partners={dashboard.topPartners} />
          </div>
          <div className={`xl:col-span-8 ${panelClassName}`}>
            <SectionHeader
              title="Visit payment history"
              subtitle="Per-visit duty credits with Partner and Individual labeling preserved."
            />
            {visibleRows.length ? (
              <div className="space-y-4">
                {visibleRows.map((row) => (
                  <VisitRow
                    key={row.rowID}
                    row={row}
                    expanded={Boolean(expandedRows[row.rowID])}
                    onToggle={toggleRowExpansion}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                title="No payment rows match this filter"
                body="Try switching the day selector to a wider time window."
              />
            )}
          </div>
        </div>

        <div className="mt-5">
          <InsightPanel insights={dashboard.insights} timeframe={timeframe} />
        </div>
      </div>
    </div>
  );
};

export default Payment;
